"use client";

import Link from "next/link";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Building2, Loader2, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import {
	fetchNearbyPropertiesUnauthenticated,
	normalizePropertyRecord,
	type NearbyPropertiesResponse,
	type NormalizedPropertyRecord,
} from "@/services/properties-api";

const SAMPLE_COUNTY = "New Castle";
const SAMPLE_STATE = "Delaware";
const SAMPLE_COUNTY_STATE = `${SAMPLE_COUNTY}, ${SAMPLE_STATE}`;
const DEFAULT_VISIBLE_COUNT = 5;
const DEFAULT_RESULTS_HREF = `/playground?countyState=${encodeURIComponent(
	SAMPLE_COUNTY_STATE
)}`;

interface SampleSearchResultsPreviewProps {
	className?: string;
	cardClassName?: string;
	compact?: boolean;
	visibleCount?: number;
	title?: string;
	description?: string;
	viewAllHref?: string;
	viewAllLabel?: string;
}

function SamplePropertyCard({
	property,
}: {
	property: NormalizedPropertyRecord;
}) {
	const location = [property.county, property.state]
		.filter(Boolean)
		.join(", ");
	const parcelHref =
		property.state && property.county && property.propertyId
			? `/${property.state.toLowerCase()}/${property.county.toLowerCase().replace(/\s+/g, "-")}/property/${encodeURIComponent(property.propertyId)}/parcel`
			: null;

	return (
		<article className="group rounded-2xl border border-[#1f3b2b] bg-[#0f1b18]/95 p-4 text-left shadow-[0_16px_40px_-28px_rgba(0,0,0,0.9)] transition-all hover:border-[#A7FF5E]/45 hover:bg-[#12231d]">
			<div className="mb-3 flex items-start justify-between gap-3">
				<div className="flex min-w-0 items-center gap-3">
					<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#A7FF5E]/12 text-[#A7FF5E] ring-1 ring-[#A7FF5E]/20">
						<Building2 className="h-4 w-4" />
					</div>
					<div className="min-w-0">
						<p className="mb-1 inline-flex rounded-full bg-[#A7FF5E]/12 px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-[0.08em] text-[#A7FF5E]">
							Property
						</p>
						<h3 className="line-clamp-2 text-sm font-bold uppercase leading-5 tracking-tight text-white">
							{property.address || "Address available"}
						</h3>
					</div>
				</div>
			</div>

			<div className="flex items-start gap-2 text-xs text-[#b9c7bd]">
				<MapPin className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#7f9688]" />
				<span>{location || SAMPLE_COUNTY_STATE}</span>
			</div>

			<div className="mt-4 border-t border-[#203329] pt-3">
				<div className="flex items-end justify-between gap-3">
					<div className="min-w-0">
						<p className="text-[0.65rem] font-bold uppercase tracking-[0.12em] text-[#7f9688]">
							Parcel Data
						</p>
					</div>
					{parcelHref ? (
						<Link
							href={parcelHref}
							className="inline-flex shrink-0 items-center gap-1 text-xs font-bold text-[#d9ffc7] hover:text-[#A7FF5E]"
						>
							View parcel
							<ArrowRight className="h-3.5 w-3.5" />
						</Link>
					) : (
						<p className="shrink-0 text-xs font-semibold text-[#7f9688]">
							Unavailable
						</p>
					)}
				</div>
			</div>
		</article>
	);
}

function SampleResultsBody({
	data,
	isLoading,
	isError,
	visibleCount,
	cardClassName,
}: {
	data?: NearbyPropertiesResponse;
	isLoading: boolean;
	isError: boolean;
	visibleCount: number;
	cardClassName?: string;
}) {
	const properties = useMemo(() => {
		return (data?.data ?? [])
			.slice(0, visibleCount)
			.map(normalizePropertyRecord);
	}, [data, visibleCount]);

	if (isLoading) {
		return (
			<div className="flex min-h-28 items-center justify-center rounded-2xl border border-dashed border-[#1f3b2b] bg-[#0f1b18]/80">
				<Loader2 className="h-5 w-5 animate-spin text-[#A7FF5E]" />
			</div>
		);
	}

	if (isError) {
		return (
			<div className="rounded-2xl border border-dashed border-[#1f3b2b] bg-[#0f1b18]/80 p-5 text-sm text-[#b9c7bd]">
				Properties are temporarily unavailable.
			</div>
		);
	}

	if (properties.length === 0) {
		return (
			<div className="rounded-2xl border border-dashed border-[#1f3b2b] bg-[#0f1b18]/80 p-5 text-sm text-[#b9c7bd]">
				No properties found yet.
			</div>
		);
	}

	return (
		<div className={cn("grid gap-3 md:grid-cols-3", cardClassName)}>
			{properties.map((property) => (
				<SamplePropertyCard
					key={property.propertyId || property.id}
					property={property}
				/>
			))}
		</div>
	);
}

export default function SampleSearchResultsPreview({
	className,
	cardClassName,
	visibleCount = DEFAULT_VISIBLE_COUNT,
	title = "Properties in New Castle",
	description = `Real properties currently available in ${SAMPLE_COUNTY_STATE}.`,
	viewAllHref = DEFAULT_RESULTS_HREF,
	viewAllLabel = "View all properties",
}: SampleSearchResultsPreviewProps) {
	const { data, isLoading, isError } = useQuery({
		queryKey: [
			"sample-search-results-preview",
			SAMPLE_COUNTY,
			SAMPLE_STATE,
		],
		queryFn: () =>
			fetchNearbyPropertiesUnauthenticated({
				county: SAMPLE_COUNTY,
				state: SAMPLE_STATE,
			}),
	});

	const visibleProperties = Math.min(data?.data?.length ?? 0, visibleCount);
	const moreCount = Math.max((data?.total ?? 0) - visibleProperties, 0);

	return (
		<section
			className={cn("text-left", className)}
			data-testid="sample-search-results-preview"
		>
			<div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
				<div>
					<p className="text-xs font-bold uppercase tracking-[0.22em] text-[#A7FF5E]/75">
						Available Properties
					</p>
					<h2 className="mt-1 font-bricolage text-2xl font-semibold text-white">
						{title}
					</h2>
					<p className="mt-1 text-sm text-[#b9c7bd]">{description}</p>
				</div>

				{moreCount > 0 && (
					<p
						className="rounded-full border border-[#A7FF5E]/20 bg-[#A7FF5E]/10 px-3 py-1 text-sm font-semibold text-[#d9ffc7]"
						data-testid="sample-search-results-more-count"
					>
						{moreCount.toLocaleString()} more properties available
					</p>
				)}
			</div>

			<SampleResultsBody
				data={data}
				isLoading={isLoading}
				isError={isError}
				visibleCount={visibleCount}
				cardClassName={cardClassName}
			/>

			<div className="mt-4 flex justify-end">
				<Link
					href={viewAllHref}
					className="inline-flex items-center gap-2 text-sm font-semibold text-[#d9ffc7] hover:text-[#A7FF5E]"
				>
					{viewAllLabel}
					<ArrowRight className="h-4 w-4" />
				</Link>
			</div>
		</section>
	);
}
